import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { averageNumericGrade, DEFAULT_GRADING_SCALE } from '../../shared/grading-scales.js';
import { ClassMembership } from '../class-membership.js';
import { ExamResultRelease } from '../exam-result-release.js';
import { Student } from '../student.js';

/** One released exam result in the student's class list. */
@Entity()
export class StudentClassExamResultDTO extends AdornisEntity {
  static override _class = 'StudentClassExamResultDTO';

  /** Identity for the client cache, which refuses to merge entity arrays it cannot tell apart. One row
   * per exam, so `examId` is it — `AdornisEntity` carries no `_id` of its own to fall back on. */
  static override resolveID(data: any) {
    return data?.examId;
  }

  @Field(() => String)
  examId!: string;

  @Field(() => String)
  title: Maybe<string>;

  @Field(() => String)
  subject: Maybe<string>;

  @Field(() => String)
  createdAt!: string;

  @Field(() => String)
  grade: Maybe<string>;

  @Field(() => Float)
  percentage: Maybe<number>;

  @Field(() => Float)
  points: Maybe<number>;

  @Field(() => Float)
  maxPoints: Maybe<number>;
}

@Entity()
export class StudentClassResultsDTO extends AdornisEntity {
  static override _class = 'StudentClassResultsDTO';

  @Field(() => [StudentClassExamResultDTO])
  exams!: StudentClassExamResultDTO[];

  /** Null when nothing is released yet, or when the scale is not numeric (A–F, percentage). */
  @Field(() => Float)
  averageGrade: Maybe<number>;
}

/**
 * A student's released results for one of their classes.
 *
 * READS ONLY `ExamResultRelease`. It touches neither `Exam` nor `Submission` — both belong to the teacher —
 * so a student never reads teacher-owned documents here and there is no name matching anywhere in the
 * path. Which rows are this student's is decided by the `studentID` foreign key a teacher confirmed during
 * distribution (see distribute-exam-results.ts); this query only has to prove the caller IS that student.
 *
 * An exam the teacher has not distributed simply has no record, which is indistinguishable from — and
 * should look exactly like — having no result yet.
 */
export const getMyClassExamResultsQuery = registerQuery({
  operationName: 'getMyClassExamResultsQuery',
  type: () => StudentClassResultsDTO,
  params: [{ name: 'classRoomID', type: () => String }],

  resolve:
    (classRoomID: string) =>
    async (_selection: BaseQLSelectionSet<StudentClassResultsDTO>): Promise<StudentClassResultsDTO> => {
      const empty = new StudentClassResultsDTO({ exams: [], averageGrade: null });

      const trimmedClassRoomID = typeof classRoomID === 'string' ? classRoomID.trim() : '';
      if (!trimmedClassRoomID) return empty;

      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      if (!currentUser?._id) return empty;

      const studentCollection = await getCollection(Student);
      const student = await studentCollection.findOne({ userID: currentUser._id });
      if (!student) return empty;

      // Membership is still required: leaving the class should end access to its results, even though the
      // release records themselves are keyed by student.
      const membership = await (await getCollection(ClassMembership)).findOne({
        classRoomID: trimmedClassRoomID,
        studentID: student._id,
        status: 'approved',
      });
      if (!membership) return empty;

      const releases = await (await getCollection(ExamResultRelease))
        .find({ classRoomID: trimmedClassRoomID, studentID: student._id })
        .sort({ releasedAt: -1 })
        .toArray();

      const exams = (releases as any[]).map(
        release =>
          new StudentClassExamResultDTO({
            _id: String(release._id),
            examId: String(release.examId),
            title: release.examTitle ?? null,
            subject: release.examSubject ?? null,
            createdAt: String(release.releasedAt ?? ''),
            grade: release.grade ?? null,
            percentage: release.percentage ?? null,
            points: release.totalPoints ?? null,
            maxPoints: release.totalMaxPoints ?? null,
          }),
      );

      // The scale is not carried on the snapshot, so the default is used. `averageNumericGrade` returns null
      // for non-numeric scales by design, and the client renders an em-dash rather than inventing a number.
      const gradeLabels = (releases as any[]).map(r => r.grade).filter((g: unknown): g is string => typeof g === 'string');
      const averageGrade = gradeLabels.length > 0 ? averageNumericGrade(gradeLabels, DEFAULT_GRADING_SCALE) : null;

      return new StudentClassResultsDTO({
        exams,
        averageGrade: averageGrade != null ? Math.round(averageGrade * 10) / 10 : null,
      });
    },
});

/**
 * The full released result for ONE exam — per-task points, the student's own answer and the feedback.
 *
 * Same access model as above: an exact lookup on (examId, this student's id). A student who was never
 * released this exam gets null, which is also what a revoked release looks like.
 */
export const getMyExamResultQuery = registerQuery({
  operationName: 'getMyExamResultQuery',
  type: () => ExamResultRelease,
  params: [{ name: 'examId', type: () => String }],

  resolve:
    (examId: string) =>
    async (_selection: BaseQLSelectionSet<ExamResultRelease>): Promise<ExamResultRelease | null> => {
      const trimmedExamId = typeof examId === 'string' ? examId.trim() : '';
      if (!trimmedExamId) return null;

      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      if (!currentUser?._id) return null;

      const student = await (await getCollection(Student)).findOne({ userID: currentUser._id });
      if (!student) return null;

      const release = await (await getCollection(ExamResultRelease)).findOne({
        examId: trimmedExamId,
        studentID: student._id,
      });
      if (!release) return null;

      const membership = await (await getCollection(ClassMembership)).findOne({
        classRoomID: (release as any).classRoomID,
        studentID: student._id,
        status: 'approved',
      });
      if (!membership) return null;

      return release as ExamResultRelease;
    },
});
